"use client"

import { useState, Fragment, useEffect } from "react"
import { Dialog, Transition } from "@headlessui/react"
import { X, RefreshCw, Share2, AlertCircle, CheckCircle2 } from "lucide-react"
import { InputCustom } from "../../components"
import adAccountsService from "../../services/adAccountsService"
import toast from "react-hot-toast"

const ReshareBMModal = ({
  isOpen,
  onClose,
  account,
  onSuccess,
  type = "BM" // default to BM, can be "BC" for TikTok
}) => {
  const [idValue, setIdValue] = useState("")
  const [isProcessing, setIsProcessing] = useState(false)
  const [reshareComplete, setReshareComplete] = useState(false)
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({})

  useEffect(() => {
    if (isOpen) {
      setIdValue("")
      setIsProcessing(false)
      setReshareComplete(false)
      setErrors({})
      if (account?._id) {
        fetchAccountData(account._id)
      }
    }
  }, [isOpen, account])

  const fetchAccountData = async (id) => {
    try {
      setLoading(true)
      const response = await adAccountsService.get(id)
      const responseData = response.data
      if (responseData?.data) {
        setFormData({
          accountName: responseData.data.account_name || "Ad Account",
          accountAlias: responseData.data.account_alias || "",
        })
      }
      setLoading(false)
    } catch (error) {
      console.error("Failed to load account data:", error)
      setLoading(false)
    }
  }

  const handleChange = (value) => {
    setIdValue(value.replace(/[^0-9]/g, "")) // only numbers
    if (errors.idValue) setErrors({ ...errors, idValue: "" })
  }

  const validateForm = () => {
    const newErrors = {}
    if (!idValue) {
      newErrors.idValue = `${type} ID is required`
    } else if (idValue.length < 5) {
      newErrors.idValue = `${type} ID must be at least 5 digits`
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleReshare = async () => {
    if (!validateForm()) return
    setIsProcessing(true)

    try {
      const fieldKey = type === "BC" ? "bcId" : "bmId"
      const response = await adAccountsService.reshareBM(account._id, { [fieldKey]: idValue })
      // same endpoint, backend handles logic
      const responseData = response.data

      if (responseData?.statusCode === 200) {
        setReshareComplete(true)
        toast.success(`${type} reshared successfully`)

        setTimeout(() => {
          if (onSuccess) onSuccess()
          onClose()
        }, 1500)
      }
    } catch (error) {
      console.error(`Failed to reshare ${type}:`, error)
      setErrors({
        api: error.response?.data?.message || `Failed to reshare ${type}`,
      })
      toast.error(error.response?.data?.message || `Failed to reshare ${type}`)
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child as={Fragment}
          enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child as={Fragment}
              enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
              leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-xl bg-white dark:bg-neutral-950 shadow-2xl transition-all border border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30">

                {/* Header */}
                <div className="relative border-b border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 p-5">
                  <div className="flex items-center justify-between">
                    <Dialog.Title className="text-xl font-[400] text-neutral-900 dark:text-white">
                      Re-Share {type}
                    </Dialog.Title>
                    <button onClick={onClose} className="p-1.5 rounded-full text-neutral-500 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800 transition-colors" type="button">
                      <X size={20} />
                    </button>
                  </div>
                </div>

                <div className="p-6">
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <RefreshCw size={30} className="animate-spin text-neutral-600 dark:text-neutral-400" />
                    </div>
                  ) : (
                    <>
                      {/* Account Info */}
                      <div className="mb-6 p-3 bg-neutral-50 dark:border-neutral-900 dark:bg-neutral-900/50 rounded-xl border border-neutral-200 dark:bg-neutral-800">
                        <div className="text-xs text-neutral-500 dark:text-neutral-400">Ad Account</div>
                        <div className="text-sm font-medium text-neutral-800 dark:text-white">{formData.accountName}</div>
                        {formData.accountAlias && (
                          <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                            Alias: {formData.accountAlias}
                          </div>
                        )}
                      </div>

                      {reshareComplete ? (
                        <div className="flex flex-col items-center justify-center py-6">
                          <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
                            <CheckCircle2 size={32} className="text-green-600 dark:text-green-400" />
                          </div>
                          <h3 className="text-lg text-center font-medium text-neutral-900 dark:text-white mb-1">
                            {type} Reshare Request Has Been Submitted Successfully
                          </h3>
                          <p className="text-sm text-neutral-500 dark:text-neutral-400">
                            {type} ID {idValue} 
                          </p>
                        </div>
                      ) : (
                        <>
                          {/* ID Input */}
                          <div className="mb-6">
                            <InputCustom
                              label={`${type} ID`}
                              placeholder={`Enter ${type} ID`}
                              type="text"
                              value={idValue}
                              onChange={handleChange}
                              icon={<Share2 size={18} className="text-neutral-500 dark:text-neutral-400" />}
                              error={errors.idValue}
                            />
                            {errors.idValue && (
                              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                                <AlertCircle size={12} />
                                {errors.idValue}
                              </p>
                            )}
                          </div>

                          {/* Submit */}
                          <button
                            type="button"
                            className="w-full h-11 px-4 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-400 flex items-center justify-center text-sm font-[400] disabled:opacity-70 disabled:cursor-not-allowed"
                            onClick={handleReshare}
                            disabled={isProcessing || !idValue}
                          >
                            {isProcessing ? (
                              <>
                                <RefreshCw size={18} className="animate-spin mr-2" />
                                Processing...
                              </>
                            ) : (
                              `Submit Request`
                            )}
                          </button>
                        </>
                      )}
                    </>
                  )}
                  {errors.api && (
                    <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/30 rounded-xl flex items-start gap-2">
                      <AlertCircle className="text-red-500 dark:text-red-400 mt-0.5 flex-shrink-0" size={16} />
                      <span className="text-xs text-red-800 dark:text-red-300">{errors.api}</span>
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default ReshareBMModal
